/* ======================================================
   Cart drawer per-product customization checkboxes.

   Checking "Alter the length" / "Full body customization"
   adds a real line item to the cart for that fee (₹50 / ₹200),
   tagged with a "For Product" property naming the exact product
   it belongs to — so the merchant can tell which item the
   request is for in the order details. It also reveals a note
   box right under that product's own checkboxes so the customer
   can type their size/length in place, instead of jumping down
   to a shared note field.

   That note is saved as a "Customization Note" property on the
   product's own cart line — so it stays attached to that exact
   product (and shows per line in cart / checkout / order), not
   mixed together with every other product's note in one shared
   field.

   Unchecking removes that fee line again. Once every
   customization checkbox for a product is unchecked, that
   product's note box is cleared and hidden. To avoid ever
   getting out of sync with the server-rendered page, every
   action re-reads the LIVE cart first and acts on that, rather
   than trusting whatever was in the page's HTML when it loaded.

   Removing the parent product itself also removes any fee lines
   attached to it, so fee lines never get orphaned in the cart.
   ====================================================== */
document.addEventListener('change', async (e) => {
  const checkbox = e.target.closest('[data-customization-toggle]');
  if (!checkbox) return;

  const feeVariantId = checkbox.getAttribute('data-fee-variant-id');
  if (!feeVariantId) return;

  const itemLabel = (checkbox.getAttribute('data-item-label') || '').trim();
  const customizationType = checkbox.getAttribute('data-customization-type') || '';
  const wasChecked = checkbox.checked;

  checkbox.disabled = true;

  try {
    const existingLine = await findFeeLine(feeVariantId, itemLabel);

    if (wasChecked) {
      if (!existingLine) {
        await addFeeLine(feeVariantId, itemLabel, customizationType);
      }
      revealNoteField(itemLabel);
    } else if (existingLine) {
      await removeFeeLine(existingLine.key);
    }

    // If every customization checkbox for this product is now
    // unchecked, clear and hide that product's note box.
    const card = getCustomizationCard(itemLabel);
    const anyStillChecked = card
      ? card.querySelectorAll('[data-customization-toggle]:checked').length > 0
      : false;

    if (!anyStillChecked) {
      await clearAndHideNoteField(itemLabel);
    }
  } catch (err) {
    console.error('Unable to update customization fee: ', err);
    checkbox.checked = !wasChecked;
  } finally {
    checkbox.disabled = false;
  }
});

/* ===== Persist the per-product note as soon as the customer
   finishes typing (on blur / change), tied to that product's own
   cart line rather than the shared cart note. ===== */
document.addEventListener('change', async (e) => {
  const textarea = e.target.closest('[data-customization-note-textarea]');
  if (!textarea) return;

  const lineKey = textarea.getAttribute('data-line-key');
  if (!lineKey) return;

  textarea.disabled = true;
  try {
    await saveNoteForLine(lineKey, textarea.value.trim());
  } catch (err) {
    console.error('Unable to save the customization note: ', err);
  } finally {
    textarea.disabled = false;
  }
});

/* ===== Removing a product also removes any fee lines tied to it,
   so a fee can never get left behind with no product to match. ===== */
document.addEventListener('click', async (e) => {
  const removeButton = e.target.closest('[data-cart-remove-item]');
  if (!removeButton) return;

  const card = removeButton.closest('.cart-drawer__product_card');
  if (!card) return;

  const checkboxes = card.querySelectorAll('[data-customization-toggle]');
  for (const checkbox of checkboxes) {
    const feeVariantId = checkbox.getAttribute('data-fee-variant-id');
    const itemLabel = (checkbox.getAttribute('data-item-label') || '').trim();
    if (!feeVariantId) continue;

    try {
      const existingLine = await findFeeLine(feeVariantId, itemLabel);
      if (existingLine) {
        await removeFeeLine(existingLine.key);
      }
    } catch (err) {
      console.error('Unable to remove attached fee: ', err);
    }
  }
});

/* ===== Read the live cart and find a fee line matching this
   variant + product label, rather than trusting page HTML. ===== */
async function findFeeLine(feeVariantId, itemLabel) {
  const cart = await getLiveCart();

  return cart.items.find((line) => {
    const lineVariantId = String(line.variant_id);
    const lineLabel = (line.properties && (line.properties['For Product'] || line.properties['_customization_for']) || '').trim();
    return lineVariantId === String(feeVariantId) && lineLabel === itemLabel;
  });
}

async function getLiveCart() {
  const response = await fetch(`${window.routes.cart_url}.js`);
  return response.json();
}

function getCustomizationCard(itemLabel) {
  const selector = '[data-cart-customization][data-item-title="' + cssEscape(itemLabel) + '"]';
  return document.querySelector(selector);
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return String(value).replace(/["\\]/g, '\\$&');
}

function revealNoteField(itemLabel) {
  const card = getCustomizationCard(itemLabel);
  if (!card) return;

  const wrap = card.querySelector('[data-customization-note-wrap]');
  if (!wrap) return;

  wrap.hidden = false;

  const textarea = wrap.querySelector('[data-customization-note-textarea]');
  if (textarea) textarea.focus();
}

async function clearAndHideNoteField(itemLabel) {
  const card = getCustomizationCard(itemLabel);
  if (!card) return;

  const wrap = card.querySelector('[data-customization-note-wrap]');
  if (!wrap) return;

  const textarea = wrap.querySelector('[data-customization-note-textarea]');
  wrap.hidden = true;

  if (textarea && textarea.value.trim() !== '') {
    const lineKey = textarea.getAttribute('data-line-key');
    textarea.value = '';
    if (lineKey) {
      await saveNoteForLine(lineKey, '');
    }
  }
}

/* ===== Save (or clear) the "Customization Note" property on a
   single product's own cart line, preserving any other
   properties already on that line. ===== */
async function saveNoteForLine(lineKey, noteValue) {
  const cart = await getLiveCart();
  const line = cart.items.find((item) => item.key === lineKey);
  if (!line) return;

  const properties = Object.assign({}, line.properties || {});
  if (noteValue) {
    properties['Customization Note'] = noteValue;
  } else {
    delete properties['Customization Note'];
  }

  const response = await fetch(window.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: lineKey,
      quantity: line.quantity,
      properties,
      sections: 'cart-drawer',
    }),
  });

  const responseJson = await response.json();

  if (!response.ok) {
    throw new Error(responseJson.description || responseJson.message || 'Unable to save the note.');
  }

  window.eventBus.emit('update:cart:drawer', responseJson);
}

async function addFeeLine(variantId, itemLabel, customizationType) {
  const response = await fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: {
        'For Product': itemLabel,
        'Customization Type': customizationType,
      },
      sections: 'cart-drawer',
    }),
  });

  const responseJson = await response.json();

  if (!response.ok) {
    throw new Error(responseJson.description || responseJson.message || 'Unable to add the fee to the cart.');
  }

  window.eventBus.emit('update:cart:drawer', responseJson);
}

async function removeFeeLine(lineKey) {
  const response = await fetch(window.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: lineKey,
      quantity: 0,
      sections: 'cart-drawer',
    }),
  });

  const responseJson = await response.json();

  if (!response.ok) {
    throw new Error(responseJson.description || responseJson.message || 'Unable to remove the fee from the cart.');
  }

  window.eventBus.emit('update:cart:drawer', responseJson);
}

/* ======================================================
   Orphan fee cleanup (safety net)

   A fee line ("Alteration Fee" / "Full body customization fee")
   only makes sense while the product named in its "For Product"
   property is still in the cart. The click handler above covers
   the drawer's remove button, but a product can also leave the
   cart through quantity - / 0, the cart page, a size change, or
   another tab — and then the fee would be left behind and the
   customer would pay for something they didn't buy.

   So after ANY cart change (we watch the cart AJAX endpoints) and
   once on page load, we read the live cart and remove every fee
   line whose product is no longer there.
   ====================================================== */
(function () {
  if (window.__feeOrphanCleanupInstalled) return;
  window.__feeOrphanCleanupInstalled = true;

  const FEE_HANDLES = ['alteration-fee', 'full-body-customization-fee'];
  const nativeFetch = window.fetch.bind(window);
  let timer = null;
  let running = false;

  function productLabel(line) {
    let label = line.product_title || '';
    if (line.variant_title && line.variant_title !== 'Default Title') {
      label += ' (' + line.variant_title + ')';
    }
    return label.trim();
  }

  function isFeeLine(line) {
    return FEE_HANDLES.indexOf(line.handle) !== -1;
  }

  function feeLabel(line) {
    const p = line.properties || {};
    return String(p['For Product'] || p['_customization_for'] || '').trim();
  }

  async function removeOrphanFees() {
    if (running) return;
    running = true;
    try {
      const res = await nativeFetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();

      const labelsInCart = new Set(
        cart.items.filter((l) => !isFeeLine(l)).map(productLabel)
      );

      const orphans = cart.items.filter(
        (l) => isFeeLine(l) && feeLabel(l) && !labelsInCart.has(feeLabel(l))
      );
      if (!orphans.length) return;

      let lastResponse = null;
      for (const fee of orphans) {
        const r = await nativeFetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: fee.key, quantity: 0, sections: 'cart-drawer' }),
        });
        if (r.ok) lastResponse = await r.json();
      }

      if (lastResponse && window.eventBus && typeof window.eventBus.emit === 'function') {
        window.eventBus.emit('update:cart:drawer', lastResponse);
      }
    } catch (err) {
      console.error('Unable to clean up orphaned fee lines: ', err);
    } finally {
      running = false;
    }
  }

  // Debounced so transient states (e.g. a size change that removes the
  // old variant and then adds the new one) settle before we judge.
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(removeOrphanFees, 700);
  }

  // Watch every cart mutation made through fetch (drawer, cart page,
  // quantity stepper, size select, etc.).
  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
        promise.then(schedule, function () {});
      }
    } catch (e) {}
    return promise;
  };

  // Heal carts that already contain an orphaned fee.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();